import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Box,
  Typography,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import UniversalBadge from './UniversalBadge';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useNavigate, useLocation } from 'react-router-dom';
import { ServiceProviderBase } from '../../types';
import { formatDate } from '../../services/help';
import { serviceProviderService } from '../../services';
import { POLLING_CONFIG } from '../../config/polling';

// Types
type SortField = 'title' | 'status' | 'createdAt';
type SortDirection = 'asc' | 'desc';

interface SortConfig {
  field: SortField;
  direction: SortDirection;
}

interface ServiceProviderTableProps {
  providers: ServiceProviderBase[];
  selectedItems: string[];
  selectedRowId: string | null;
  searchTerm: string;
  statusFilter: string;
  isWide: boolean;
  enablePolling?: boolean;
  pollingInterval?: number;
  onSelectItem: (event: React.ChangeEvent<HTMLInputElement>, id: string) => void;
  onSelectAll: (checked: boolean) => void;
  onContextMenu: (event: React.MouseEvent, providerId: string, status: string) => void;
  onProvidersUpdate?: (providers: ServiceProviderBase[]) => void;
  onError?: (error: string) => void;
}

// Status-Übersetzung erfolgt jetzt über die StatusBadge-Komponente

const ServiceProviderTable: React.FC<ServiceProviderTableProps> = ({
  providers,
  selectedItems,
  selectedRowId,
  searchTerm,
  statusFilter,
  isWide,
  enablePolling = POLLING_CONFIG.ENABLE_POLLING,
  pollingInterval = POLLING_CONFIG.SERVICE_PROVIDER_INTERVAL,
  onSelectItem,
  onSelectAll,
  onContextMenu,
  onProvidersUpdate,
  onError
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'title',
    direction: 'asc'
  });
  const [pollingProviders, setPollingProviders] = useState<ServiceProviderBase[]>(providers);

  // ⚡ GLOBALES POLLING SYSTEM ⚡
  useEffect(() => {
    if (!enablePolling) return;

    const pollData = async () => {
      try {
        const freshProviders = await serviceProviderService.getServiceProviders();

        // Vergleiche alle wichtigen Felder, um Änderungen zu erkennen
        const hasChanges = freshProviders.some((freshProvider, index) => {
          const currentProvider = pollingProviders[index];
          if (!currentProvider) return true;

          return freshProvider.status !== currentProvider.status ||
            freshProvider.title !== currentProvider.title ||
            freshProvider._id !== currentProvider._id ||
            freshProvider.createdAt !== currentProvider.createdAt;
        });

        if (hasChanges || freshProviders.length !== pollingProviders.length) {
          setPollingProviders(freshProviders);
          onProvidersUpdate?.(freshProviders);
        }
      } catch (error) {
        console.error('Service Provider Polling error:', error);
        onError?.('Fehler beim Aktualisieren der Daten');
      }
    };

    // Registriere bei globalem Polling-Manager
    const { PollingManager } = require('../../config/polling');
    const pollingManager = PollingManager.getInstance();

    pollingManager.register('service-providers', pollData);

    // Legacy-Fallback (falls globales System deaktiviert)
    const legacyInterval = !POLLING_CONFIG.ENABLE_POLLING ? setInterval(pollData, pollingInterval) : null;

    return () => {
      pollingManager.unregister('service-providers');

      if (legacyInterval) {
        clearInterval(legacyInterval);
      }
    };
  }, [enablePolling, pollingInterval, pollingProviders, onProvidersUpdate, onError]);

  // Update internal state when external providers change
  useEffect(() => {
    setPollingProviders(providers);
  }, [providers]);

  // Use polling data if polling is enabled, otherwise use prop data
  const activeProviders = enablePolling ? pollingProviders : providers;

  // Filtered and sorted providers
  const filteredAndSortedProviders = useMemo(() => {
    let filtered = activeProviders.filter(provider => {
      const matchesSearch = provider.title.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || provider.status === statusFilter;
      return matchesSearch && matchesStatus;
    });

    filtered.sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortConfig.field) {
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt || '').getTime();
          bValue = new Date(b.createdAt || '').getTime();
          break;
        default:
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [activeProviders, searchTerm, statusFilter, sortConfig]);

  // Event handlers
  const handleSort = (field: SortField) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleRowClick = (providerId: string) => {
    navigate(`/dienstleister/${providerId}`);
  };

  const isSelected = (id: string): boolean => {
    return location.pathname.includes(`/dienstleister/${id}`);
  };

  if (filteredAndSortedProviders.length === 0) {
    return (
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4
      }}>
        <Typography color="text.secondary" sx={{ fontSize: '1rem', textAlign: 'center' }} data-testid="providers-table-empty-state">
          {searchTerm || statusFilter !== 'all'
            ? 'Keine Dienstleister gefunden.'
            : 'Noch keine Dienstleister vorhanden.'
          }
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ borderLeft: '4px solid transparent' }}>
            <TableCell padding="checkbox">
              <Checkbox
                checked={selectedItems.length === filteredAndSortedProviders.length && filteredAndSortedProviders.length > 0}
                onChange={(e) => onSelectAll(e.target.checked)}
                inputProps={{ 'data-testid': 'providers-table-select-all-checkbox' } as any}
                sx={{ padding: '0.25rem' }}
              />
            </TableCell>
            <TableCell
              sx={{
                cursor: 'pointer',
                userSelect: 'none',
                fontSize: '1rem',
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
              }}
              onClick={() => handleSort('title')}
              data-testid="providers-table-header-name"
            >
              {t('providers:table.name')} {sortConfig.field === 'title' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
            </TableCell>
            <TableCell
              sx={{
                cursor: 'pointer',
                userSelect: 'none',
                fontSize: '1rem',
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
              }}
              onClick={() => handleSort('status')}
              data-testid="providers-table-header-status"
            >
              {t('providers:table.status')} {sortConfig.field === 'status' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
            </TableCell>
            {isWide && (
              <TableCell
                sx={{
                  cursor: 'pointer',
                  userSelect: 'none',
                  fontSize: '1rem',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
                }}
                onClick={() => handleSort('createdAt')}
                data-testid="providers-table-header-created"
              >
                {t('providers:table.createdAt')} {sortConfig.field === 'createdAt' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : ''}
              </TableCell>
            )}
            {isWide && (
              <TableCell align="right" sx={{ fontSize: '1rem' }} data-testid="providers-table-header-actions">
                {t('providers:table.actions')}
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredAndSortedProviders.map((provider) => (
            <TableRow
              key={provider._id}
              hover
              onContextMenu={(e) => onContextMenu(e, provider._id, provider.status)}
              onClick={() => handleRowClick(provider._id)}
              data-testid={`providers-table-row-${provider._id}`}
              sx={{
                cursor: 'pointer',
                bgcolor: isSelected(provider._id)
                  ? 'primary.main'
                  : selectedRowId === provider._id
                    ? 'rgba(0, 0, 0, 0.04)'
                    : 'inherit',
                '&:hover': {
                  bgcolor: isSelected(provider._id)
                    ? 'primary.dark'
                    : selectedRowId === provider._id
                      ? 'rgba(0, 0, 0, 0.08)'
                      : 'rgba(0, 0, 0, 0.04)'
                },
                borderLeft: isSelected(provider._id) ? '4px solid' : '4px solid transparent',
                borderLeftColor: isSelected(provider._id) ? 'primary.blue' : 'transparent',
                height: '4.25rem',
                transition: enablePolling ? 'background-color 0.2s ease' : 'none'
              }}
            >
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedItems.includes(provider._id)}
                  onChange={(e) => onSelectItem(e, provider._id)}
                  onClick={(e) => e.stopPropagation()}
                  inputProps={{ 'data-testid': `providers-table-row-${provider._id}-checkbox` } as any}
                  sx={{
                    padding: '0.25rem',
                    color: 'primary.blue',
                    '&.Mui-checked': {
                      color: 'primary.blue',
                    }
                  }}
                />
              </TableCell>
              <TableCell sx={{
                fontSize: '1rem',
                fontWeight: 400
              }}>
                <Tooltip title={provider.title} arrow>
                  <Box sx={{
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    maxWidth: isWide ? '200px' : '180px',
                    lineHeight: 1.2,
                    maxHeight: '2.4em' // 2 lines * 1.2 line-height
                  }}>
                    {provider.title}
                  </Box>
                </Tooltip>
              </TableCell>
              <TableCell sx={{ fontSize: '1rem' }}>
                <UniversalBadge type="status" value={provider.status} data-testid={`providers-table-row-${provider._id}-status-badge`} />
              </TableCell>
              {isWide && (
                <TableCell sx={{
                  fontSize: '1rem'
                }}>
                  {provider.createdAt ? formatDate(provider.createdAt) : '-'}
                </TableCell>
              )}
              {isWide && (
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onContextMenu(e, provider._id, provider.status);
                    }}
                    data-testid={`providers-table-row-${provider._id}-menu-button`}
                    sx={{
                      '&:hover': {
                        bgcolor: isSelected(provider._id)
                          ? 'rgba(255, 255, 255, 0.1)'
                          : 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  >
                    <MoreVertIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ServiceProviderTable; 